import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { trpc } from "@/admin/lib/trpc";
import { Button } from "@nosible/shared/components/ui/button";
import { Label } from "@nosible/shared/components/ui/label";
import { EmailPreview } from "../components/EmailPreview";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@nosible/shared/components/ui/alert-dialog";
import { Mail, Send, Eye, Users, CheckCircle, Loader2, ChevronLeft } from "lucide-react";

type EmailPreferenceType = "researchUpdates" | "productUpdates" | "apiUsage";

export function AdminEmailSend() {
  const location = useLocation();
  const navigate = useNavigate();
  const { subject, greeting, markdown, previewHtml } = (location.state || {}) as {
    subject?: string;
    greeting?: string;
    markdown?: string;
    previewHtml?: string;
  };

  const [selectedTypes, setSelectedTypes] = useState<EmailPreferenceType[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [sendResult, setSendResult] = useState<{
    total: number;
    jobId: string;
  } | null>(null);

  if (!subject || !markdown) {
    navigate("/emails/custom");
    return null;
  }

  const { data: emailTypes } = trpc.email.getEmailPreferenceTypes.useQuery();

  const { data: recipientData, isLoading: isLoadingRecipients } =
    trpc.email.previewRecipientList.useQuery(
      { emailTypes: selectedTypes },
      { enabled: selectedTypes.length > 0 }
    );

  const sendMutation = trpc.email.createCustomEmailJob.useMutation({
    onSuccess: (data) => {
      setSendResult({
        total: data.totalRecipients,
        jobId: data.jobId,
      });
      setShowConfirmDialog(false);
    },
  });

  const handleTypeToggle = (type: EmailPreferenceType) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleSend = () => {
    if (markdown && subject && selectedTypes.length > 0) {
      sendMutation.mutate({
        emailTypes: selectedTypes,
        markdown,
        subject,
        greeting: greeting || undefined,
      });
    }
  };

  const canSend = selectedTypes.length > 0;

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <Button
            onClick={() => navigate("/emails/custom")}
            variant="outline"
            size="sm"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Draft
          </Button>
        </div>
        <h1 className="text-2xl font-bold text-silica font-display flex items-center gap-2 mt-4">
          <Send className="h-6 w-6" />
          Select Recipients
        </h1>
        <p className="text-chromium-mist mt-1">
          Choose which users should receive this email based on their preferences.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Recipient Selection */}
        <div className="space-y-6">
          {/* Email Type Selection */}
          <div className="bg-slate-core rounded-lg p-4">
            <Label className="text-silica font-medium mb-3 block">
              <Users className="h-4 w-4 inline mr-2" />
              Target Audience
            </Label>
            <div className="space-y-2">
              {emailTypes?.map((type) => (
                <label
                  key={type.value}
                  className="flex items-center gap-3 p-3 rounded-md bg-obsidian hover:bg-obsidian/80 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedTypes.includes(type.value)}
                    onChange={() => handleTypeToggle(type.value)}
                    className="h-4 w-4 rounded border-chromium-mist text-neon-green focus:ring-neon-green"
                  />
                  <span className="text-silica">{type.label}</span>
                </label>
              ))}
            </div>

            {selectedTypes.length > 0 && (
              <div className="mt-4 p-3 bg-obsidian rounded-md">
                <p className="text-sm text-chromium-mist">
                  {isLoadingRecipients ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Calculating recipients...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-neon-green" />
                      <strong className="text-neon-green">
                        {recipientData?.recipientCount ?? 0}
                      </strong>{" "}
                      users will receive this email
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Email Summary */}
          <div className="bg-slate-core rounded-lg p-4 space-y-3">
            <Label className="text-silica font-medium block">Email Summary</Label>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-chromium-mist">Subject:</span>
                <p className="text-silica font-medium">{subject}</p>
              </div>
              <div>
                <span className="text-chromium-mist">Greeting:</span>
                <p className="text-silica">{greeting || "Hi"}</p>
              </div>
            </div>
          </div>

          {/* Send Button */}
          <Button
            onClick={() => setShowConfirmDialog(true)}
            disabled={!canSend}
            className="w-full bg-neon-green text-obsidian hover:bg-neon-green/90"
          >
            <Send className="h-4 w-4 mr-2" />
            Send Email to {recipientData?.recipientCount ?? 0} Users
          </Button>

          {/* Send Result */}
          {sendResult && (
            <div className="p-4 rounded-lg bg-neon-green/20">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-neon-green" />
                <span className="font-medium text-silica">
                  Emails queued successfully!
                </span>
              </div>
              <p className="text-sm text-chromium-mist mb-3">
                {sendResult.total} emails have been queued for delivery.
              </p>
              <Button
                onClick={() => navigate(`/emails/jobs/${sendResult.jobId}`)}
                variant="outline"
                size="sm"
              >
                View Job Details
              </Button>
            </div>
          )}
        </div>

        {/* Right Column: Preview */}
        <div className="bg-slate-core rounded-lg p-4">
          <Label className="text-silica font-medium mb-3 block">
            <Eye className="h-4 w-4 inline mr-2" />
            Email Preview
          </Label>
          {previewHtml ? (
            <EmailPreview html={previewHtml} className="h-[calc(100vh-300px)]" />
          ) : (
            <div className="flex items-center justify-center h-[400px] bg-obsidian rounded-lg border border-dashed border-chromium-mist/30">
              <div className="text-center text-chromium-mist">
                <Eye className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No preview available</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirm Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="bg-slate-core border-chromium-mist/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-silica">Confirm Send</AlertDialogTitle>
            <AlertDialogDescription className="text-chromium-mist">
              You are about to send this email to{" "}
              <strong className="text-neon-green">
                {recipientData?.recipientCount ?? 0} users
              </strong>
              . This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-obsidian text-silica hover:bg-obsidian/80">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSend}
              disabled={sendMutation.isPending}
              className="bg-neon-green text-obsidian hover:bg-neon-green/90"
            >
              {sendMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send to {recipientData?.recipientCount ?? 0} users
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
